import { useState } from 'react'

import type { Control, UseFormRegisterReturn } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  HStack,
  Input,
  SecondaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import {
  KOMA_DIVIDER_COLOR_TOKEN,
  isKomaDividerBetween,
} from '~/models/komaDivider'
import { komaNumberTimeRange } from '~/models/komaToTime'
import {
  constrainNumberInput,
  wrapNumberInputChange,
} from '~/models/numberInputConstraint'
import type { BlockBidCreateFormInput } from '~/pages/SpotBidding/models/blockBidCreateSchema'
import {
  BLOCK_SLOT_VOLUME_CONSTRAINT,
  effectiveSlotVolume,
  slotExceedsQtyMax,
  slotVolumeMapOf,
} from '~/pages/SpotBidding/models/blockBidCreateSchema'

type SlotVolume = { timeCode: number; volumeMwh: number }

type Props = {
  /** 対象 leg の form path prefix。 */
  legPrefix: 'primaryLeg' | 'secondaryLeg'
  control: Control<BlockBidCreateFormInput>
  /** 「全コマ一括」入力欄の register 結果 (= `<leg>.volumeMwh`)。 */
  baseVolumeRegistration: UseFormRegisterReturn
  /**
   * 現在の「全コマ一括」値 (= コマ別上書きが無いコマの表示値)。
   * 未入力なら `undefined` (= コマ別欄も空で出す)。
   */
  baseVolumeMwh: number | undefined
  /** 選択中のコマ範囲。 未選択・不正 (to < from) なら表は出さず案内文を出す。 */
  timeCodeFrom: number | undefined
  timeCodeTo: number | undefined
  /** 「全コマ一括」欄のエラー文言 (= `<leg>.volumeMwh` の zod エラー)。 */
  baseErrorMessage: string | undefined
  /** コマ別上書きのエラー文言 (= `<leg>.slotVolumes` 配下の zod エラー)。 */
  slotErrorMessage: string | undefined
  /**
   * settings「入札制限」の 上限数量(1札) MW (= 当 leg の エリア × 売買 で解決済み)。
   * `undefined` = 制限未設定 or エリア/売買 未確定で、 その場合は判定しない。
   */
  qtyMaxMw: number | undefined
}

/**
 * ブロック入札の入札量入力 (常時コマ別表 + 全コマ一括、 design `spot.html` の
 * `.blk-vol-head` / `.blk-vol-table` 相当)。
 *
 * ## 2 層モデル
 *
 * 入札量は「全コマ一括値 (`<leg>.volumeMwh`)」と「コマ別上書き
 * (`<leg>.slotVolumes`)」の 2 層で持ち、 各コマの実効値は
 * [[effectiveSlotVolume]] (上書き優先) で決まる。 JEPX 制度上ブロック入札は
 * コマ毎に異なる入札量を指定できる (AC-BSP-030a)。
 *
 * 「全コマ同一量にする」チェックボックスは**作らない** — 常にコマ別表を出し、
 * 見出し行の「全コマ一括」入力 +「一括適用」で一括反映と個別上書きを両立する
 * (2026-08-18 Des 裁定。 design 正本が 2026-07-24 時点でこの形)。
 *
 * ## 入力欄の値
 *
 * 表示値は「上書きがあればその値、 無ければ全コマ一括値」。 入力途中の生テキスト
 * (空文字・"1." など) は編集中の 1 セルだけ [[editingDraft]] に保持し、 blur で
 * 正規化した表示に戻す (= 消した瞬間に一括値へ跳ね返って打ち直せない、 を防ぐ)。
 * 空にした場合は上書きを削除する (= 一括値に戻す。 design の `commitEdit` と同じ規則)。
 *
 * ## 入札制限 (上限数量)
 *
 * settings「入札制限」の 上限数量(1札) を超えるコマがあれば **該当行に** 違反文言を出す
 * (AC-BSP-030a「該当行にエラーを表示する」。 旧実装は表の下 1 行にまとめて出しており
 * どのコマが超過しているか分からなかった)。 判定は [[slotExceedsQtyMax]] (= per-slot 述語の
 * 唯一の実装) に委ね、 submit ブロック ([[blockLegExceedsPerBidLimit]]、 AC-BSP-104) も
 * 同じ述語を使うので drift しない。 文言も通常入札の行内ヒントと同じ
 * `biddingCommon.bidLimitHint.volumeMaxError` を使う (価格軸が [[BlockBidCreateModal]] で
 * `bidLimitHint.price*Error` を再利用しているのと同じ扱い)。
 * この表示自体は事前ヒントで submit をブロックしない (ブロックは親 handleSave、 AC-BSP-104)。
 * QTY 軸を 3 軸すべてとあわせて再検証する SoT は backend 側の
 * TransitionState ゲート (AC-BDL-008 / AC-BSP-104、 block_bid.go
 * ensureWithinBlockBidLimits。 Create/Update 側にも同じ QTY 軸のみの
 * ensureWithinBidVolumeLimit がある)。 上限数量(コマ計) (AC-BSP-080 / bid-limits.md
 * 1-2) の合算チェックは、 他札 (同一 org の spot 札・他グループの block 札) との合算が
 * 要るためこの表では判定できず、 server 側にのみ
 * BlockBidUsecase.ensureWithinBidVolumeTotalLimit として実装済み (T-87)。 この表への
 * 行内表示・client 側事前ブロックは引き続き別スコープ。
 */
export const BlockLegVolumeSection = ({
  legPrefix,
  control,
  baseVolumeRegistration,
  baseVolumeMwh,
  timeCodeFrom,
  timeCodeTo,
  baseErrorMessage,
  slotErrorMessage,
  qtyMaxMw,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // 編集中セルの生テキスト (= 1 セルのみ。 blur で破棄する)。
  const [editingDraft, setEditingDraft] = useState<
    { timeCode: number; text: string } | undefined
  >(undefined)

  const hasRange =
    timeCodeFrom !== undefined &&
    timeCodeTo !== undefined &&
    Number.isFinite(timeCodeFrom) &&
    Number.isFinite(timeCodeTo) &&
    timeCodeTo >= timeCodeFrom
  const slotCodes = hasRange
    ? Array.from(
        { length: timeCodeTo - timeCodeFrom + 1 },
        (_, i) => timeCodeFrom + i,
      )
    : []

  return (
    <FieldRoot invalid={baseErrorMessage !== undefined} required>
      {/* 見出し行 (全コマ一括) と コマ別表 は同じ `slotVolumes` を読み書きするので
          Controller は 1 つに束ねる (= 同一 name を二重に register しない)。 */}
      <Controller
        control={control}
        name={`${legPrefix}.slotVolumes`}
        render={({ field }) => {
          const slotVolumes: SlotVolume[] = field.value ?? []
          const overrides = slotVolumeMapOf(slotVolumes)

          /** コマ `timeCode` の上書きを設定 / 解除する (絶対コマ番号キー)。 */
          const commit = (timeCode: number, raw: string): void => {
            const text = raw.trim()
            const parsed = Number(text)
            const rest = slotVolumes.filter((s) => s.timeCode !== timeCode)
            if (text === '' || !Number.isFinite(parsed)) {
              // 空 / 不正 → 上書き削除 (= 全コマ一括値に戻る)。
              field.onChange(rest)
              return
            }
            field.onChange(
              [...rest, { timeCode, volumeMwh: parsed }].sort(
                (a, b) => a.timeCode - b.timeCode,
              ),
            )
          }

          return (
            <>
              {/* 見出し行: 「入札量」ラベル (左) と 全コマ一括の操作 (右端) を
                  同一行に置く (design `.blk-vol-head`)。 */}
              <Flex
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={3}
                width="100%"
              >
                <FieldLabel>{t('page:biddingBlock.form.volumeMwh')}</FieldLabel>
                <HStack gap={2}>
                  {/* design `bidding/spot.html:5854` は `<span class="form-label
                      blk-vol-default-label">全コマ一括</span>`。 modal 内の
                      `.form-label` は同ファイル :384-388 で md (14px) / 700 /
                      high-emphasis。 実装は sm / mid-emphasis で、 入力欄に付く
                      ラベルなのに本文より弱く見えていた (#7636 の 8080 突合)。 */}
                  <Text textStyle="filterLabel" color="Text.highEmphasis">
                    {t('page:biddingBlock.form.volumeBulkLabel')}
                  </Text>
                  <Input
                    width="120px"
                    textAlign="right"
                    aria-label={t('page:biddingBlock.form.volumeBulkAriaLabel')}
                    max={BLOCK_SLOT_VOLUME_CONSTRAINT.max}
                    min={BLOCK_SLOT_VOLUME_CONSTRAINT.min}
                    step={BLOCK_SLOT_VOLUME_CONSTRAINT.step}
                    type="number"
                    {...baseVolumeRegistration}
                    onChange={wrapNumberInputChange(
                      baseVolumeRegistration,
                      BLOCK_SLOT_VOLUME_CONSTRAINT,
                    )}
                  />
                  {/* 操作の説明は DS Tooltip で出す (native `title` は使わない。 UI 辞書
                      `components/tooltip.md`)。 */}
                  <ButtonTooltip
                    content={t('page:biddingBlock.form.volumeBulkApplyHint')}
                  >
                    <SecondaryButton
                      // 「個別設定をクリアし全コマを一括値で揃える」 (design `.blk-vol-apply`)。
                      //
                      // 一括値が未入力 (= 空欄は `valueAsNumber` で NaN。 schema は NaN を未入力として扱う) のときは押させない。
                      // 揃える先の値が無いまま上書きだけを消すと、 最大 48 コマぶんの手入力が
                      // 復元手段なく失われて表の全セルが空になる。 design 正本
                      // (`design/bidding/spot.html` の `_volState.volume`) は base が常に
                      // 有効値を持つ前提で、 この状態自体が起きない。
                      // DS の Button は native `disabled` でなく `aria-disabled` で
                      // 表現するため click は届く。 ハンドラ側でも同じ条件で弾く。
                      disabled={!Number.isFinite(baseVolumeMwh)}
                      onClick={() => {
                        if (!Number.isFinite(baseVolumeMwh)) return
                        setEditingDraft(undefined)
                        field.onChange([])
                      }}
                      size="sm"
                      type="button"
                    >
                      {t('page:biddingBlock.form.volumeBulkApply')}
                    </SecondaryButton>
                  </ButtonTooltip>
                </HStack>
              </Flex>
              <FieldErrorText>{baseErrorMessage}</FieldErrorText>

              <Box width="100%">
                {slotCodes.length === 0 ? (
                  <Text color="Text.midEmphasis" fontSize="sm">
                    {t('page:biddingBlock.form.volumeTableRangeHint')}
                  </Text>
                ) : (
                  <Box
                    overflowY="auto"
                    width="100%"
                    maxHeight="40vh"
                    borderWidth="1px"
                    borderColor="Border.lowEmphasis"
                    borderRadius="md"
                  >
                    {/* [DS差異] DS の Table component (TableRoot / TableCell 等) ではなく
                        生の table markup を使う。 本表はデータ表示用の表ではなく「行数が
                        コマ範囲で変わる入力グリッド」で、 40vh スクロール枠 + sticky ヘッダ +
                        `table-layout: fixed` (入力欄の内容で列幅を揺らさない) が要る。
                        同じ理由で生 table を使う先例が
                        `pages/Supply48Data/components/Supply48DataGrid.tsx` にある。
                        寸法・構成は design 正本 `design/bidding/spot.html` の `.blk-vol-table`
                        (3 列 / sticky thead / max-height 40vh) に合わせ、 色・余白・textStyle は
                        DS token で指定する (= DS recipe を打ち消す独自 style は重ねない)。 */}
                    <Box
                      as="table"
                      width="100%"
                      style={{ tableLayout: 'fixed' }}
                    >
                      <Box as="thead">
                        <Box as="tr">
                          <Box
                            as="th"
                            textStyle="tableHeader"
                            position="sticky"
                            zIndex={1}
                            top="0"
                            width="64px"
                            color="Text.midEmphasis"
                            textAlign="left"
                            backgroundColor="Surface.tableHeader"
                            paddingX={2}
                            paddingY={1}
                          >
                            {t('page:biddingBlock.form.volumeTableSlot')}
                          </Box>
                          <Box
                            as="th"
                            textStyle="tableHeader"
                            position="sticky"
                            zIndex={1}
                            top="0"
                            width="140px"
                            color="Text.midEmphasis"
                            textAlign="left"
                            backgroundColor="Surface.tableHeader"
                            paddingX={2}
                            paddingY={1}
                          >
                            {t('page:biddingBlock.form.volumeTableTime')}
                          </Box>
                          <Box
                            as="th"
                            textStyle="tableHeader"
                            position="sticky"
                            zIndex={1}
                            top="0"
                            color="Text.midEmphasis"
                            textAlign="right"
                            backgroundColor="Surface.tableHeader"
                            paddingX={2}
                            paddingY={1}
                          >
                            {t('page:biddingBlock.form.volumeMwh')}
                          </Box>
                        </Box>
                      </Box>
                      <Box as="tbody">
                        {slotCodes.map((timeCode, slotIndex) => {
                          // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)
                          const isKomaDividerRow = isKomaDividerBetween(
                            timeCode,
                            slotCodes[slotIndex + 1],
                          )
                          const timeRangeLabel = komaNumberTimeRange(
                            timeCode,
                            '〜',
                          )
                          const effective = effectiveSlotVolume(
                            overrides,
                            timeCode,
                            // watch の生値は空欄 (valueAsNumber) で NaN なので、 未入力は undefined に揃える。
                            Number.isFinite(baseVolumeMwh)
                              ? baseVolumeMwh
                              : undefined,
                          )
                          const displayValue =
                            editingDraft?.timeCode === timeCode
                              ? editingDraft.text
                              : effective !== undefined
                                ? String(effective)
                                : ''
                          // このコマだけの上限数量(1札)違反 (AC-BSP-030a)。
                          const slotOverLimitMessage = slotExceedsQtyMax(
                            effective ?? Number.NaN,
                            qtyMaxMw,
                          )
                            ? t(
                                'page:biddingCommon.bidLimitHint.volumeMaxError',
                                {
                                  max: qtyMaxMw,
                                },
                              )
                            : undefined
                          return (
                            <Box key={timeCode} as="tr">
                              <Box
                                as="td"
                                textStyle="tableCell"
                                color="Text.midEmphasis"
                                borderColor={
                                  isKomaDividerRow
                                    ? KOMA_DIVIDER_COLOR_TOKEN
                                    : 'Border.lowEmphasis'
                                }
                                borderBottomWidth="1px"
                                css={{ fontVariantNumeric: 'tabular-nums' }}
                                paddingX={2}
                                paddingY={1}
                              >
                                {String(timeCode).padStart(2, '0')}
                              </Box>
                              <Box
                                as="td"
                                textStyle="tableCell"
                                color="Text.midEmphasis"
                                borderColor={
                                  isKomaDividerRow
                                    ? KOMA_DIVIDER_COLOR_TOKEN
                                    : 'Border.lowEmphasis'
                                }
                                borderBottomWidth="1px"
                                css={{ fontVariantNumeric: 'tabular-nums' }}
                                paddingX={2}
                                paddingY={1}
                              >
                                {timeRangeLabel}
                              </Box>
                              <Box
                                as="td"
                                borderColor={
                                  isKomaDividerRow
                                    ? KOMA_DIVIDER_COLOR_TOKEN
                                    : 'Border.lowEmphasis'
                                }
                                borderBottomWidth="1px"
                                paddingX={2}
                                paddingY={1}
                              >
                                <Input
                                  textAlign="right"
                                  aria-label={t(
                                    'page:biddingBlock.form.volumeSlotAriaLabel',
                                    { timeCode },
                                  )}
                                  error={slotOverLimitMessage}
                                  max={BLOCK_SLOT_VOLUME_CONSTRAINT.max}
                                  min={BLOCK_SLOT_VOLUME_CONSTRAINT.min}
                                  onBlur={() => setEditingDraft(undefined)}
                                  onChange={(e) => {
                                    const next = constrainNumberInput(
                                      e.target.value,
                                      BLOCK_SLOT_VOLUME_CONSTRAINT,
                                    )
                                    setEditingDraft({ timeCode, text: next })
                                    commit(timeCode, next)
                                  }}
                                  size="sm"
                                  step={BLOCK_SLOT_VOLUME_CONSTRAINT.step}
                                  type="number"
                                  value={displayValue}
                                />
                              </Box>
                            </Box>
                          )
                        })}
                      </Box>
                    </Box>
                  </Box>
                )}
                {slotErrorMessage !== undefined && (
                  <Text marginTop={1} color="Text.negativeHigh" fontSize="sm">
                    {slotErrorMessage}
                  </Text>
                )}
              </Box>
            </>
          )
        }}
      />
    </FieldRoot>
  )
}
